// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  ActionIcon,
  SimpleGrid,
  Tabs,
  Loader,
  Center,
  Table,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconSend,
  IconFileInvoice,
  IconCurrencyDollar,
  IconAlertTriangle,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { StatCard } from '../../components/StatCard';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useInvoices,
  useInvoiceSummary,
  useCreateInvoice,
  useUpdateInvoice,
  useSendInvoice,
  useRecordPayment,
  useVoidInvoice,
} from '../../hooks/useInvoicing';
import { formatCurrency, formatDate } from '../../utils/format';
import type {
  Invoice,
  InvoiceLineItem,
  CreateInvoiceInput,
  PaymentMethod,
} from '../../types/invoice';

const INVOICE_STATUS_COLORS: Record<string, string> = {
  draft: 'gray',
  sent: 'blue',
  viewed: 'cyan',
  partial: 'yellow',
  paid: 'green',
  overdue: 'red',
  cancelled: 'gray',
  written_off: 'orange',
};

interface LineItemRow {
  description: string;
  quantity: number;
  unitPrice: number;
  jobId: string;
  jobName: string;
}

const emptyLineItem = (): LineItemRow => ({
  description: '',
  quantity: 1,
  unitPrice: 0,
  jobId: '',
  jobName: '',
});

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Form state for create/edit
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [issueDate, setIssueDate] = useState<string | null>(new Date().toISOString().split('T')[0] ?? null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [lineItems, setLineItems] = useState<LineItemRow[]>([emptyLineItem()]);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Net 30');

  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState<string | null>(new Date().toISOString().split('T')[0] ?? null);
  const [paymentMethod, setPaymentMethod] = useState<string>('ach');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Void form state
  const [voidReason, setVoidReason] = useState('');

  const queryStatus = statusFilter === 'all' ? undefined : statusFilter ?? undefined;
  const { data: invoicesData, loading } = useInvoices({ page, perPage: 25, status: queryStatus });
  const { data: rawSummary } = useInvoiceSummary();
  const summary = rawSummary as any;

  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();
  const sendInvoice = useSendInvoice();
  const recordPayment = useRecordPayment();
  const voidInvoice = useVoidInvoice();

  const subtotal = useMemo(
    () => lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0),
    [lineItems],
  );
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  const resetCreateForm = () => {
    setClientId('');
    setClientName('');
    setIssueDate(new Date().toISOString().split('T')[0] ?? null);
    setDueDate(null);
    setLineItems([emptyLineItem()]);
    setTaxRate(0);
    setNotes('');
    setTerms('Net 30');
    setSelectedInvoice(null);
  };

  const openCreateModal = () => {
    resetCreateForm();
    setCreateModalOpen(true);
  };

  const openEditModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setClientId(invoice.clientId);
    setClientName(invoice.clientName);
    setIssueDate(invoice.issueDate);
    setDueDate(invoice.dueDate);
    setLineItems(
      invoice.lineItems.map((li) => ({
        description: li.description,
        quantity: li.quantity,
        unitPrice: li.unitPrice,
        jobId: li.jobId ?? '',
        jobName: li.jobName ?? '',
      })),
    );
    setTaxRate(invoice.taxRate);
    setNotes(invoice.notes);
    setTerms(invoice.terms);
    setCreateModalOpen(true);
  };

  const openPaymentModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setPaymentAmount(invoice.amountDue);
    setPaymentDate(new Date().toISOString().split('T')[0] ?? null);
    setPaymentMethod('ach');
    setPaymentReference('');
    setPaymentNotes('');
    setPaymentModalOpen(true);
  };

  const openVoidModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setVoidReason('');
    setVoidModalOpen(true);
  };

  const handleAddLineItem = () => {
    setLineItems([...lineItems, emptyLineItem()]);
  };

  const handleRemoveLineItem = (index: number) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_: any, i) => i !== index));
  };

  const handleLineItemChange = (index: number, field: keyof LineItemRow, value: string | number) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value } as LineItemRow;
    setLineItems(updated);
  };

  const handleCreateOrUpdate = async () => {
    if (!clientName.trim() || !issueDate || !dueDate) {
      notifications.show({ title: 'Validation Error', message: 'Client name, issue date, and due date are required.', color: 'red' });
      return;
    }

    const validLineItems = lineItems.filter((li) => li.description.trim() && li.quantity > 0);
    if (validLineItems.length === 0) {
      notifications.show({ title: 'Validation Error', message: 'At least one line item is required.', color: 'red' });
      return;
    }

    const input: CreateInvoiceInput = {
      clientId: clientId || clientName.toLowerCase().replace(/\s+/g, '-'),
      clientName,
      issueDate: issueDate ?? '',
      dueDate: dueDate ?? '',
      lineItems: validLineItems.map((li): { description: string; quantity: number; unitPrice: number; jobId?: string; jobName?: string } => ({
        description: li.description,
        quantity: li.quantity,
        unitPrice: li.unitPrice,
        jobId: li.jobId || undefined,
        jobName: li.jobName || undefined,
      })),
      taxRate,
      notes: notes || undefined,
      terms: terms || undefined,
    };

    try {
      if (selectedInvoice) {
        await updateInvoice.mutateAsync({ id: selectedInvoice.id, ...input });
        notifications.show({ title: 'Invoice Updated', message: 'Invoice has been updated successfully.', color: 'green' });
      } else {
        await createInvoice.mutateAsync(input);
        notifications.show({ title: 'Invoice Created', message: 'New invoice has been created.', color: 'green' });
      }
      setCreateModalOpen(false);
      resetCreateForm();
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to save invoice.', color: 'red' });
    }
  };

  const handleSendInvoice = async (invoice: Invoice) => {
    try {
      await sendInvoice.mutateAsync({ id: invoice.id });
      notifications.show({ title: 'Invoice Sent', message: `Invoice ${invoice.invoiceNumber} has been sent.`, color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to send invoice.', color: 'red' });
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice || !paymentDate || paymentAmount <= 0) return;

    try {
      await recordPayment.mutateAsync({
        invoiceId: selectedInvoice.id,
        amount: paymentAmount,
        paymentDate: paymentDate ?? '',
        method: paymentMethod as PaymentMethod,
        reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      });
      notifications.show({ title: 'Payment Recorded', message: `Payment of ${formatCurrency(paymentAmount)} recorded.`, color: 'green' });
      setPaymentModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to record payment.', color: 'red' });
    }
  };

  const handleVoidInvoice = async () => {
    if (!selectedInvoice || !voidReason.trim()) return;

    try {
      await voidInvoice.mutateAsync({ id: selectedInvoice.id, reason: voidReason });
      notifications.show({ title: 'Invoice Voided', message: `Invoice ${selectedInvoice.invoiceNumber} has been voided.`, color: 'orange' });
      setVoidModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to void invoice.', color: 'red' });
    }
  };

  const columns: Column<Invoice>[] = [
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      sortable: true,
      width: 130,
      render: (inv) => (
        <Text fw={600} size="sm">
          {inv.invoiceNumber}
        </Text>
      ),
    },
    {
      key: 'clientName',
      label: 'Client',
      sortable: true,
      render: (inv) => inv.clientName,
    },
    {
      key: 'issueDate',
      label: 'Issue Date',
      sortable: true,
      width: 120,
      render: (inv) => formatDate(inv.issueDate),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortable: true,
      width: 120,
      render: (inv) => formatDate(inv.dueDate),
    },
    {
      key: 'total',
      label: 'Total',
      width: 120,
      render: (inv) => <CurrencyDisplay amount={inv.total} size="sm" />,
    },
    {
      key: 'amountDue',
      label: 'Amount Due',
      width: 120,
      render: (inv) => <CurrencyDisplay amount={inv.amountDue} size="sm" colored />,
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (inv) => <StatusBadge status={inv.status} colorMap={INVOICE_STATUS_COLORS} />,
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Invoices & Accounts Receivable"
        subtitle={invoicesData ? `${invoicesData.length} invoices` : undefined}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={openCreateModal}>
            New Invoice
          </Button>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Outstanding"
          value={formatCurrency(summary?.totalOutstanding ?? 0)}
          icon={<IconCurrencyDollar size={24} />}
          color="blue"
        />
        <StatCard
          title="Overdue"
          value={formatCurrency(summary?.totalOverdue ?? 0)}
          icon={<IconAlertTriangle size={24} />}
          color="red"
        />
        <StatCard
          title="Paid This Month"
          value={formatCurrency(summary?.paidThisMonth ?? 0)}
          icon={<IconCheck size={24} />}
          color="green"
        />
        <StatCard
          title="Total Revenue YTD"
          value={formatCurrency(summary?.totalRevenueYtd ?? 0)}
          icon={<IconFileInvoice size={24} />}
          color="violet"
        />
      </SimpleGrid>

      <Tabs value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }}>
        <Tabs.List>
          <Tabs.Tab value="all">All</Tabs.Tab>
          <Tabs.Tab value="draft">Draft</Tabs.Tab>
          <Tabs.Tab value="sent">Sent</Tabs.Tab>
          <Tabs.Tab value="overdue">Overdue</Tabs.Tab>
          <Tabs.Tab value="paid">Paid</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {loading ? (
        <Center py="xl"><Loader /></Center>
      ) : !invoicesData?.length ? (
        <EmptyState
          title="No invoices found"
          message="Create your first invoice to start tracking accounts receivable."
          action={{ label: 'Create Invoice', onClick: openCreateModal }}
        />
      ) : (
        <DataTable<Invoice>
          columns={columns}
          data={invoicesData}
          loading={loading}
          pagination={{
            page,
            pageSize: 25,
            total: invoicesData.length,
            onPageChange: setPage,
          }}
          onRowClick={(inv) => navigate(`/invoicing/${inv.id}`)}
          getRowKey={(inv) => inv.id}
          emptyMessage="No invoices found"
          actions={(inv) => (
            <Group gap={4} wrap="nowrap">
              {inv.status === 'draft' && (
                <>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="blue"
                    onClick={() => openEditModal(inv)}
                    title="Edit"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="teal"
                    onClick={() => handleSendInvoice(inv)}
                    title="Send"
                  >
                    <IconSend size={16} />
                  </ActionIcon>
                </>
              )}
              {['sent', 'viewed', 'partial', 'overdue'].includes(inv.status) && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="green"
                  onClick={() => openPaymentModal(inv)}
                  title="Record Payment"
                >
                  <IconCurrencyDollar size={16} />
                </ActionIcon>
              )}
              {!['paid', 'cancelled', 'written_off'].includes(inv.status) && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="red"
                  onClick={() => openVoidModal(inv)}
                  title="Void"
                >
                  <IconX size={16} />
                </ActionIcon>
              )}
            </Group>
          )}
        />
      )}

      {/* Create/Edit Invoice Modal */}
      <Modal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title={selectedInvoice ? 'Edit Invoice' : 'Create Invoice'}
        size="xl"
      >
        <Stack gap="md">
          <SimpleGrid cols={2}>
            <TextInput
              label="Client ID"
              placeholder="Auto-generated if empty"
              value={clientId}
              onChange={(e) => setClientId(e.currentTarget.value)}
            />
            <TextInput
              label="Client Name"
              placeholder="Client or company name"
              value={clientName}
              onChange={(e) => setClientName(e.currentTarget.value)}
              required
            />
          </SimpleGrid>

          <SimpleGrid cols={2}>
            <DateInput
              label="Issue Date"
              value={issueDate}
              onChange={setIssueDate}
              required
            />
            <DateInput
              label="Due Date"
              value={dueDate}
              onChange={setDueDate}
              required
              minDate={issueDate ?? undefined}
            />
          </SimpleGrid>

          <Text fw={600} size="sm" mt="sm">Line Items</Text>
          <Paper withBorder p="sm">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Description</Table.Th>
                  <Table.Th w={90}>Qty</Table.Th>
                  <Table.Th w={120}>Unit Price</Table.Th>
                  <Table.Th w={120}>Amount</Table.Th>
                  <Table.Th w={50} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lineItems.map((item, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <TextInput
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.currentTarget.value)}
                        variant="unstyled"
                        styles={{ input: { borderBottom: '1px solid var(--mantine-color-gray-3)' } }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        value={item.quantity}
                        onChange={(val) => handleLineItemChange(index, 'quantity', Number(val) || 0)}
                        min={0}
                        variant="unstyled"
                        styles={{ input: { borderBottom: '1px solid var(--mantine-color-gray-3)', textAlign: 'right' } }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        value={item.unitPrice}
                        onChange={(val) => handleLineItemChange(index, 'unitPrice', Number(val) || 0)}
                        min={0}
                        decimalScale={2}
                        prefix="$"
                        variant="unstyled"
                        styles={{ input: { borderBottom: '1px solid var(--mantine-color-gray-3)', textAlign: 'right' } }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text ta="right" size="sm" fw={500}>
                        {formatCurrency(item.quantity * item.unitPrice)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleRemoveLineItem(index)}
                        disabled={lineItems.length <= 1}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Button variant="subtle" size="xs" icon={<IconPlus size={14} />} mt="xs" onClick={handleAddLineItem}>
              Add Line Item
            </Button>
          </Paper>

          <SimpleGrid cols={2}>
            <NumberInput
              label="Tax Rate (%)"
              value={taxRate}
              onChange={(val) => setTaxRate(Number(val) || 0)}
              min={0}
              max={100}
              decimalScale={2}
              suffix="%"
            />
            <Stack gap={4} justify="flex-end">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Subtotal:</Text>
                <Text size="sm" fw={500}>{formatCurrency(subtotal)}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Tax:</Text>
                <Text size="sm" fw={500}>{formatCurrency(taxAmount)}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="md" fw={700}>Total:</Text>
                <Text size="md" fw={700}>{formatCurrency(total)}</Text>
              </Group>
            </Stack>
          </SimpleGrid>

          <Textarea
            label="Notes"
            placeholder="Additional notes for the client"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            rows={3}
          />

          <TextInput
            label="Payment Terms"
            placeholder="e.g., Net 30"
            value={terms}
            onChange={(e) => setTerms(e.currentTarget.value)}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdate}
              loading={createInvoice.isPending || updateInvoice.isPending}
            >
              {selectedInvoice ? 'Update Invoice' : 'Create Invoice'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Record Payment Modal */}
      <Modal
        opened={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        title={`Record Payment - ${selectedInvoice?.invoiceNumber ?? ''}`}
        size="md"
      >
        <Stack gap="md">
          {selectedInvoice && (
            <Paper withBorder p="sm" bg="gray.0">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Invoice Total:</Text>
                <Text size="sm" fw={500}>{formatCurrency(selectedInvoice.total)}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Amount Due:</Text>
                <Text size="sm" fw={700} c="red">{formatCurrency(selectedInvoice.amountDue)}</Text>
              </Group>
            </Paper>
          )}

          <NumberInput
            label="Payment Amount"
            value={paymentAmount}
            onChange={(val) => setPaymentAmount(Number(val) || 0)}
            min={0.01}
            max={selectedInvoice?.amountDue}
            decimalScale={2}
            prefix="$"
            required
          />

          <DateInput
            label="Payment Date"
            value={paymentDate}
            onChange={setPaymentDate}
            required
          />

          <Select
            label="Payment Method"
            value={paymentMethod}
            onChange={(v) => setPaymentMethod(v ?? 'ach')}
            data={[
              { value: 'check', label: 'Check' },
              { value: 'ach', label: 'ACH' },
              { value: 'wire', label: 'Wire Transfer' },
              { value: 'credit_card', label: 'Credit Card' },
              { value: 'other', label: 'Other' },
            ]}
            required
          />

          <TextInput
            label="Reference Number"
            placeholder="Check number, transaction ID, etc."
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.currentTarget.value)}
          />

          <Textarea
            label="Notes"
            placeholder="Optional payment notes"
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.currentTarget.value)}
            rows={2}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setPaymentModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleRecordPayment}
              loading={recordPayment.isPending}
              icon={<IconCurrencyDollar size={16} />}
            >
              Record Payment
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Void Invoice Modal */}
      <Modal
        opened={voidModalOpen}
        onClose={() => setVoidModalOpen(false)}
        title={`Void Invoice - ${selectedInvoice?.invoiceNumber ?? ''}`}
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This action cannot be undone. The invoice will be marked as cancelled.
          </Text>

          <Textarea
            label="Reason for voiding"
            placeholder="Provide a reason for cancelling this invoice"
            value={voidReason}
            onChange={(e) => setVoidReason(e.currentTarget.value)}
            rows={3}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setVoidModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleVoidInvoice}
              loading={voidInvoice.isPending}
              disabled={!voidReason.trim()}
            >
              Void Invoice
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
